/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

/**
 * Hindi strings — initial machine-assisted draft, pending translation-vendor
 * review (tracked as dependency E8). Missing keys fall back to English.
 */
const HOME_TASKS = 'घर के कार्य';
const THERAPY_PLANS = 'थेरेपी योजनाएँ';
const CLIENT = 'क्लिनिक';
const STATUS = 'स्थिति';
const PLAN = 'योजना';
const MRR = 'MRR';
const TRIAL = 'परीक्षण';
const SUBSCRIPTION = 'सदस्यता';
const ACTION = 'क्रिया';
const THERAPISTS = 'चिकित्सक';
const ACTIVE_CLIENTS = 'सक्रिय क्लाइंट';
const LEGAL_NAME = 'कानूनी नाम';
const CONTACT_EMAIL = 'संपर्क ईमेल';
const CONTACT_PHONE = 'संपर्क फ़ोन';
const FILTER_BY_STATUS = 'स्थिति फ़िल्टर';
const ALL_STATUS = 'सभी स्थिति';

export default {
  common: {
    appName: 'TherapyOS',
    loading: 'लोड हो रहा है…',
    save: 'परिवर्तन सहेजें',
    cancel: 'रद्द करें',
    signOut: 'लॉग आउट',
    search: 'खोजें…',
    loadError: 'डेटा लोड नहीं हो सका। कृपया पुनः प्रयास करें।',
    saveError: 'सहेजा नहीं जा सका। कृपया फ़ॉर्म जाँचें और पुनः प्रयास करें।',
  },
  topbar: {
    back: 'वापस जाएँ',
    welcome: 'वापसी पर स्वागत है',
    welcomeName: 'वापसी पर स्वागत है, {{name}}',
    addNew: 'नया जोड़ें',
    notifications: 'सूचनाएँ',
    noNotifications: 'कोई नई सूचना नहीं',
    toggleTheme: 'डार्क मोड टॉगल करें',
  },
  status: {
    active: 'सक्रिय',
    inactive: 'निष्क्रिय',
    discharged: 'छुट्टी दी गई',
    scheduled: 'निर्धारित',
    in_progress: 'चालू',
    completed: 'पूर्ण',
    cancelled: 'रद्द',
    no_show: 'अनुपस्थित',
  },
  goalStatus: {
    not_started: 'शुरू नहीं',
    emerging: 'उभरता',
    in_progress: 'चालू',
    achieved: 'प्राप्त',
    maintenance: 'अनुरक्षण',
    discontinued: 'बंद',
  },
  measurementType: {
    frequency: 'आवृत्ति',
    percent_accuracy: 'प्रतिशत सटीकता',
    duration: 'अवधि',
    latency: 'विलंबता',
    trials_to_criterion: 'मानदंड तक प्रयास',
    rating_scale: 'रेटिंग पैमाना',
    independence_level: 'स्वतंत्रता स्तर',
    prompt_level: 'संकेत स्तर',
    anecdotal: 'वर्णनात्मक',
  },
  sessionType: {
    individual: 'व्यक्तिगत',
    group: 'समूह',
    teletherapy: 'टेलीथेरेपी',
    assessment: 'मूल्यांकन',
    review: 'समीक्षा',
    supervision: 'पर्यवेक्षण',
    home_visit: 'गृह भेंट',
  },
  therapist: {
    dashboard: {
      myClients: 'मेरे क्लाइंट',
      upcoming: 'आगामी सत्र',
      plans: THERAPY_PLANS,
    },
    clients: {
      title: 'मेरे क्लाइंट',
      count: '{{count}} क्लाइंट',
      searchPlaceholder: 'क्लाइंट खोजें…',
      empty: 'अभी तक कोई क्लाइंट नियुक्त नहीं।',
    },
    notes: {
      title: 'सत्र नोट',
      subtitle: 'अपने सत्र दर्ज करें',
      count: 'दर्ज करने के लिए {{count}} सत्र',
      empty: 'दर्ज करने के लिए कोई सत्र नहीं।',
      open: 'नोट खोलें',
    },
    schedule: {
      title: 'अनुसूची',
      subtitle: 'आपका साप्ताहिक सत्र कैलेंडर',
      add: 'नया सत्र',
      empty: 'कोई आगामी सत्र नहीं।',
      noneDay: 'कोई सत्र नहीं',
      prevWeek: 'पिछला सप्ताह',
      nextWeek: 'अगला सप्ताह',
      thisWeek: 'इस सप्ताह',
    },
    availability: {
      title: 'उपलब्धता',
      subtitle: 'आपके साप्ताहिक कार्य घंटे',
      save: 'सहेजें',
      saved: 'उपलब्धता सहेजी गई।',
      saveError: 'उपलब्धता सहेजी नहीं जा सकी। कृपया पुनः प्रयास करें।',
      invalid:
        'अपनी समय विंडो जाँचें — समाप्ति प्रारंभ के बाद होनी चाहिए और विंडो ओवरलैप नहीं होनी चाहिए।',
      addWindow: 'विंडो जोड़ें',
      removeWindow: 'विंडो हटाएँ',
      none: 'उपलब्ध नहीं',
      days: {
        mon: 'सोमवार',
        tue: 'मंगलवार',
        wed: 'बुधवार',
        thu: 'गुरुवार',
        fri: 'शुक्रवार',
        sat: 'शनिवार',
        sun: 'रविवार',
      },
    },
    sessions: {
      title: 'सत्र',
      subtitle: 'आज के सत्र',
      empty: 'कोई सत्र निर्धारित नहीं।',
      colClient: 'क्लाइंट',
      colType: 'प्रकार',
      colTime: 'समय',
      colStatus: 'स्थिति',
      colActions: 'क्रियाएँ',
      start: 'शुरू करें',
      complete: 'पूर्ण करें',
      cancel: 'रद्द करें',
      reschedule: 'पुनर्निर्धारित करें',
      note: 'सत्र नोट',
      rate: 'रेट करें',
      rescheduleTitle: 'सत्र पुनर्निर्धारित करें',
      rescheduleSubtitle:
        'नया समय चुनें। चिकित्सक के अन्य सत्रों से टकराव वाला समय अस्वीकार कर दिया जाएगा।',
    },
    rate: {
      title: 'सत्र रेट करें',
      subtitle: 'सहभागिता, सहयोग और संदर्भ',
      engagement: 'सहभागिता',
      cooperation: 'सहयोग',
      note: 'संदर्भ टिप्पणी',
      unrated: 'रेट नहीं किया',
      submit: 'रेटिंग सहेजें',
    },
    note: {
      title: 'सत्र नोट',
      back: 'सत्रों पर वापस जाएँ',
      signed: 'हस्ताक्षरित',
      trials: 'लक्ष्य परीक्षण डेटा',
      correct: 'सही',
      incorrect: 'गलत',
      prompted: 'संकेतित',
      subjective: 'व्यक्तिपरक',
      objective: 'वस्तुपरक',
      assessment: 'मूल्यांकन',
      plan: 'योजना',
      saveDraft: 'ड्राफ्ट सहेजें',
      sign: 'हस्ताक्षर करें और लॉक करें',
    },
    plans: {
      title: THERAPY_PLANS,
      subtitle: 'व्यक्तिगत थेरेपी योजनाएँ',
      count: '{{count}} सक्रिय योजनाएँ',
      searchPlaceholder: 'नाम से योजनाएँ खोजें…',
      empty: 'अभी तक कोई योजना नहीं।',
      progress: 'कुल प्रगति',
      review: 'समीक्षा',
      reviewDate: 'समीक्षा तिथि',
      currentGoals: 'वर्तमान लक्ष्य',
      moreGoals: '+{{count}} और',
      viewDetails: 'विवरण देखें',
      updateProgress: 'प्रगति अपडेट करें',
      detailsTitle: 'थेरेपी योजना विवरण',
      client: 'क्लाइंट',
      program: 'कार्यक्रम',
      startDate: 'आरंभ तिथि',
      goals: 'लक्ष्य',
    },
    planForm: {
      newPlan: 'नई योजना',
      fromTemplate: 'टेम्पलेट से शुरू करें',
      fromTemplateNone: 'कोई टेम्पलेट नहीं — खाली शुरू करें',
      subtitle: 'किसी क्लाइंट के लिए थेरेपी योजना और उसके लक्ष्य बनाएँ।',
      client: 'क्लाइंट',
      selectClient: 'क्लाइंट चुनें',
      name: 'योजना का नाम',
      programType: 'कार्यक्रम प्रकार',
      startDate: 'आरंभ तिथि',
      reviewDate: 'समीक्षा तिथि',
      goals: 'लक्ष्य',
      addGoal: 'लक्ष्य जोड़ें',
      goalN: 'लक्ष्य {{n}}',
      goalTitle: 'लक्ष्य',
      domain: 'क्षेत्र',
      selectDomain: 'क्षेत्र चुनें',
      measurementType: 'मापन विधि',
      selectMeasurementType: 'विधि चुनें',
      measure: 'मापन विवरण',
      baseline: 'आधार रेखा',
      target: 'लक्ष्य मान',
      goalReviewDate: 'लक्ष्य समीक्षा तिथि',
      approachTags: 'दृष्टिकोण टैग',
      approachTagsHint: 'अल्पविराम से अलग, जैसे PECS, DTT',
      subTargets: 'उप-लक्ष्य',
      subTargetsHint: '; से अलग करें — > के बाद चरण जोड़ें, जैसे अनुकरण > मॉडल, संकेत',
      fromLibrary: 'लाइब्रेरी से',
      allDomains: 'सभी क्षेत्र',
      pickDomain: 'क्षेत्र फ़िल्टर',
      pickGoal: 'लक्ष्य चुनें…',
      domains: {
        SLT: 'वाक् एवं भाषा',
        OT: 'व्यावसायिक चिकित्सा',
        ABA: 'व्यवहार विश्लेषण',
        PT: 'भौतिक चिकित्सा',
        SPECED: 'विशेष शिक्षा',
      },
      removeGoal: 'लक्ष्य हटाएँ',
      submit: 'योजना बनाएँ',
    },
    plan: {
      back: 'योजनाओं पर वापस जाएँ',
      goals: 'वर्तमान लक्ष्य',
      noGoals: 'इस योजना में अभी कोई लक्ष्य नहीं।',
      updateStatus: 'लक्ष्य स्थिति अपडेट करें',
      subTargets: 'उप-लक्ष्य',
      reviewBy: '{{date}} तक समीक्षा',
      statusNote: 'स्थिति-परिवर्तन नोट (वैकल्पिक)',
      statusNotePlaceholder: 'स्थिति क्यों बदल रही है?',
      history: 'स्थिति इतिहास',
      showHistory: 'स्थिति इतिहास',
      hideHistory: 'इतिहास छिपाएँ',
      noHistory: 'अभी तक कोई स्थिति परिवर्तन दर्ज नहीं।',
      historyEntry: '{{from}} → {{to}}',
      historyInitial: '{{to}} पर सेट',
      saveAsTemplate: 'टेम्पलेट के रूप में सहेजें',
      exportPdf: 'PDF निर्यात करें',
      exporting: 'निर्यात हो रहा है…',
      saveTemplateTitle: 'टेम्पलेट के रूप में सहेजें',
      saveTemplateSubtitle: 'भविष्य की योजनाओं में इन लक्ष्यों का पुनः उपयोग करें।',
      templateName: 'टेम्पलेट नाम',
    },
    templates: {
      title: 'टेम्पलेट',
      subtitle: 'आपके क्लिनिक के लिए पुन: प्रयोज्य लक्ष्य सेट',
      empty: 'अभी कोई टेम्पलेट नहीं। किसी योजना को टेम्पलेट के रूप में सहेजें।',
      goalCount: '{{count}} लक्ष्य',
      delete: 'टेम्पलेट हटाएँ',
    },
    collaborators: {
      title: 'सहयोगी चिकित्सक',
      none: 'अभी कोई सहयोगी नहीं।',
      add: 'सहयोगी जोड़ें…',
      remove: 'सहयोगी हटाएँ',
    },
    review: {
      title: 'नैदानिक समीक्षाएँ',
      due: 'समीक्षा देय',
      dueBanner: '{{count}} योजनाएँ नैदानिक समीक्षा के लिए देय हैं',
      record: 'समीक्षा दर्ज करें',
      empty: 'अभी तक कोई समीक्षा दर्ज नहीं की गई।',
      modalTitle: 'नैदानिक समीक्षा दर्ज करें',
      modalSubtitle: 'समीक्षा परिणाम दर्ज करें और अगली समीक्षा तिथि निर्धारित करें।',
      outcome: 'परिणाम',
      outcome_continue: 'योजना जारी रखें',
      outcome_revise: 'योजना संशोधित करें',
      outcome_discharge: 'डिस्चार्ज',
      summary: 'सारांश',
      recommendations: 'सिफ़ारिशें (वैकल्पिक)',
      nextDate: 'अगली समीक्षा तिथि',
      submit: 'समीक्षा सहेजें',
    },
    homeTasks: {
      link: HOME_TASKS,
      back: 'मेरे क्लाइंट पर वापस जाएँ',
      title: HOME_TASKS,
      subtitle: 'घर-कार्यक्रम कार्य असाइन करें और समीक्षा करें',
    },
  },
  parent: {
    selectChild: 'बच्चा',
    noData: 'अभी तक कोई डेटा दर्ज नहीं',
    dashboard: {
      title: 'स्वागत है',
      subtitle: 'आपके बच्चों की थेरेपी एक नज़र में',
      empty: 'अभी तक कोई बच्चा नामांकित नहीं।',
      progress: 'कुल प्रगति',
      viewProgress: 'प्रगति देखें →',
      recentReports: 'हाल की रिपोर्टें',
    },
    achievements: {
      dayStreak: '{{count}} दिन की लय',
      completed: '{{count}} पूर्ण',
      badge: {
        firstStep: 'पहला कदम',
        threeDay: '3-दिन की लय',
        weekWarrior: 'सप्ताह योद्धा',
        fiftyDone: '50 पूर्ण',
      },
    },
    progress: {
      title: 'प्रगति',
      subtitle: 'समय के साथ लक्ष्य सटीकता',
      empty: 'अभी तक कोई प्रगति डेटा दर्ज नहीं।',
    },
    sessions: {
      title: 'सत्र',
      subtitle: 'आपके बच्चों का थेरेपी कार्यक्रम',
      empty: 'कोई सत्र निर्धारित नहीं।',
      colChild: 'बच्चा',
      colType: 'प्रकार',
      colTime: 'समय',
      colStatus: 'स्थिति',
    },
    billing: {
      title: 'बिलिंग',
      subtitle: 'आपके इनवॉइस और भुगतान',
      empty: 'अभी तक कोई इनवॉइस नहीं।',
      payNow: 'अभी भुगतान करें',
      payNowTitle: 'इनवॉइस का भुगतान करें',
      payNowSubtitle: 'बकाया राशि का सुरक्षित भुगतान करें (Razorpay)।',
      payConfirm: 'अभी भुगतान करें',
      paying: 'प्रोसेस हो रहा है…',
      payError: 'भुगतान संसाधित नहीं हो सका। कृपया पुनः प्रयास करें।',
    },
    reports: {
      title: 'रिपोर्ट',
      count: '{{count}} रिपोर्ट',
      empty: 'अभी तक आपके साथ कोई रिपोर्ट साझा नहीं की गई।',
    },
    homeTasks: {
      title: HOME_TASKS,
      subtitle: 'आज का घर पर थेरेपी अभ्यास',
      dailyProgress: 'दैनिक प्रगति',
      completedCount: '{{total}} में से {{done}} आज पूर्ण',
      completed: 'पूर्ण ✓',
      dueToday: 'आज देय',
      markDone: 'पूर्ण के रूप में चिह्नित करें',
      empty: 'अभी तक कोई घर का कार्य नहीं।',
    },
  },
  admin: {
    dashboard: {
      activeClients: 'सक्रिय क्लाइंट',
      therapists: 'चिकित्सक',
      sessions: 'सत्र',
      sessionsThisMonth: 'इस माह के सत्र',
      avgProgress: 'औसत योजना प्रगति',
      plans: THERAPY_PLANS,
      distribution: 'क्लाइंट वितरण',
      distributionSub: 'थेरेपी प्रकार अनुसार',
      sessionsTrend: 'सत्र',
      sessionsTrendSub: 'पिछले 6 माह',
      performance: 'चिकित्सक प्रदर्शन',
      performanceSub: 'शीर्ष प्रदर्शक',
      sessionsUnit: '{{count}} सत्र',
      upcoming: 'आगामी सत्र',
      upcomingSub: 'आज का कार्यक्रम',
      noUpcoming: 'कोई आगामी सत्र नहीं।',
      noData: 'अभी कोई डेटा नहीं।',
      viewAll: 'सभी देखें',
      activity: 'हाल की गतिविधि',
      activitySub: 'हाल ही में पूर्ण सत्र',
      noActivity: 'कोई हाल की गतिविधि नहीं।',
      activitySession: '{{client}} ने एक सत्र पूरा किया',
    },
    clients: {
      title: 'क्लाइंट प्रबंधन',
      subtitle: 'आपके क्लिनिक में नामांकित बच्चे',
      count: 'कुल {{count}} क्लाइंट',
      add: 'क्लाइंट जोड़ें',
      edit: 'क्लाइंट संपादित करें',
      addSubtitle: 'नया क्लाइंट रिकॉर्ड बनाएँ',
      empty: 'अभी तक कोई क्लाइंट नहीं।',
      searchPlaceholder: 'क्लाइंट, निदान, चिकित्सक खोजें…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      age: 'आयु {{age}}',
      colName: 'क्लाइंट',
      colDiagnosis: 'निदान',
      colTherapist: 'चिकित्सक',
      colSessions: 'सत्र',
      colProgress: 'प्रगति',
      colStatus: 'स्थिति',
      colActions: 'क्रियाएँ',
      deleteTitle: 'क्लाइंट हटाएँ?',
      deleteBody:
        '{{name}} और उनके सभी रिकॉर्ड स्थायी रूप से हटाएँ। इसे पूर्ववत नहीं किया जा सकता।',
    },
    clientDetail: {
      back: 'क्लाइंट सूची पर वापस',
      notFound: 'क्लाइंट नहीं मिला।',
      age: 'आयु',
      therapist: 'चिकित्सक',
      guardian: 'अभिभावक',
      contact: 'संपर्क',
      plans: THERAPY_PLANS,
      noPlans: 'अभी कोई योजना नहीं।',
      sessions: 'हाल के सत्र',
      noSessions: 'अभी कोई सत्र नहीं।',
      billing: 'बिलिंग',
      noInvoices: 'अभी कोई चालान नहीं।',
    },
    sessions: {
      title: 'सत्र',
      count: '{{count}} सत्र',
      empty: 'कोई सत्र निर्धारित नहीं।',
      searchPlaceholder: 'प्रकार या स्थान से खोजें…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      schedule: 'सत्र निर्धारित करें',
      scheduleSubtitle: 'नया थेरेपी सत्र बुक करें',
      colClient: 'क्लाइंट',
      colTherapist: 'चिकित्सक',
      colType: 'प्रकार',
      colTime: 'समय',
      colStatus: 'स्थिति',
    },
    users: {
      title: 'टीम सदस्य',
      count: 'कुल {{count}} उपयोगकर्ता',
      invite: 'उपयोगकर्ता आमंत्रित करें',
      inviteTitle: 'एक टीम सदस्य आमंत्रित करें',
      inviteSubtitle: 'उन्हें पासवर्ड सेट करने के लिए एक ईमेल प्राप्त होगा।',
      empty: 'अभी तक कोई टीम सदस्य नहीं। शुरू करने के लिए अपने पहले उपयोगकर्ता को आमंत्रित करें।',
      enable: 'सक्षम करें',
      disable: 'अक्षम करें',
      name: 'पूरा नाम',
      email: 'ईमेल',
      role: 'भूमिका',
      inviteError: 'आमंत्रण नहीं भेजा जा सका। कृपया पुनः प्रयास करें।',
      col: {
        name: 'नाम',
        email: 'ईमेल पता',
        roles: 'भूमिकाएं',
        status: 'स्थिति',
        lastLogin: 'अंतिम लॉगिन',
      },
      role_CLINIC_ADMIN: 'क्लिनिक एडमिन',
      role_THERAPIST: 'चिकित्सक',
      role_FINANCE: 'वित्त',
      status_invited: 'आमंत्रित',
      status_active: 'सक्रिय',
      status_disabled: 'अक्षम',
    },
    therapists: {
      title: 'चिकित्सक प्रबंधन',
      subtitle: 'आपके क्लिनिक के नैदानिक कर्मचारी',
      registered: '{{count}} चिकित्सक पंजीकृत',
      add: 'चिकित्सक जोड़ें',
      addSubtitle: 'नया चिकित्सक प्रोफ़ाइल बनाएँ',
      empty: 'अभी तक कोई चिकित्सक नहीं।',
      searchPlaceholder: 'चिकित्सक खोजें…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      clients: 'क्लाइंट',
      sessions: 'सत्र',
      rating: 'रेटिंग',
      joined: 'शामिल हुए {{date}}',
    },
    finance: {
      title: 'वित्त',
      count: '{{count}} इनवॉइस',
    },
    reports: {
      title: 'रिपोर्ट',
      count: '{{count}} रिपोर्ट',
    },
    invite: {
      title: 'अभिभावक को आमंत्रित करें',
      subtitle: 'इस बच्चे के लिए अभिभावक लॉगिन बनाएं',
      intro: '{{child}} के अभिभावक को पैरेंट पोर्टल तक पहुंच का आमंत्रण भेजें।',
      parentName: 'अभिभावक का पूरा नाम',
      email: 'अभिभावक का ईमेल',
      submit: 'आमंत्रण भेजें',
      error: 'आमंत्रण नहीं भेजा जा सका। ईमेल पहले से उपयोग में हो सकता है।',
      success: '{{email}} को आमंत्रण ईमेल भेजा गया।',
      linkLabel: 'सक्रियण लिंक',
      copy: 'कॉपी',
      copied: 'कॉपी किया गया',
      note: 'हमने यह लिंक अभिभावक को ईमेल कर दिया है। आप इसे कॉपी करके भी साझा कर सकते हैं — इससे वे पासवर्ड सेट कर साइन इन कर सकते हैं, और यह 7 दिनों में समाप्त होगा।',
    },
  },
  form: {
    client: {
      fullName: 'पूरा नाम',
      age: 'आयु',
      primaryDiagnosis: 'निदान',
      email: 'ईमेल',
      phone: 'फ़ोन',
      assignTherapist: 'नियुक्त चिकित्सक',
      noTherapist: 'कोई चिकित्सक नियुक्त नहीं',
      submit: 'क्लाइंट जोड़ें',
      guardianName: 'अभिभावक का नाम',
      inviteParentNow: 'अभिभावक लॉगिन भी बनाएँ',
      inviteParentHint: 'हम अभिभावक को पैरेंट पोर्टल के लिए सक्रियण लिंक ईमेल करेंगे।',
      parentName: 'अभिभावक का पूरा नाम',
      parentEmail: 'अभिभावक का ईमेल',
    },
    therapist: {
      fullName: 'पूरा नाम',
      email: 'ईमेल पता',
      phone: 'फ़ोन',
      specialty: 'विशेषज्ञता',
      specialisations: 'विशेषज्ञताएँ',
      specialisationsHint: 'अल्पविराम से अलग, जैसे ASD, स्पीच, OT',
      joinedOn: 'शामिल होने की तिथि',
      submit: 'चिकित्सक जोड़ें',
    },
    session: {
      client: 'क्लाइंट',
      selectClient: 'क्लाइंट चुनें',
      therapist: 'चिकित्सक',
      anyTherapist: 'कोई भी उपलब्ध चिकित्सक',
      type: 'सत्र प्रकार',
      typeHint: 'जैसे स्पीच थेरेपी',
      anyType: 'कोई भी प्रकार',
      start: 'दिनांक और समय',
      submit: 'सत्र निर्धारित करें',
    },
    reschedule: {
      start: 'नया दिनांक और समय',
      submit: 'पुनर्निर्धारित करें',
      conflict: 'वह समय इस चिकित्सक के किसी अन्य सत्र से टकराता है।',
    },
    invoice: {
      client: 'क्लाइंट',
      selectClient: 'क्लाइंट चुनें',
      service: 'सेवा',
      serviceCustom: 'कस्टम राशि',
      amount: 'राशि (₹)',
      amountHint: 'जैसे 2500',
      dueDate: 'देय तिथि',
      notes: 'टिप्पणियाँ',
      notesHint: 'जैसे जून स्पीच-थेरेपी सत्र',
      submit: 'इनवॉइस बनाएँ',
    },
    payment: {
      balance: 'बकाया राशि: {{amount}}',
      amount: 'राशि (₹)',
      method: 'भुगतान विधि',
      selectMethod: 'विधि चुनें',
      submit: 'भुगतान दर्ज करें',
    },
    homeTask: {
      title: 'कार्य',
      titleHint: 'जैसे खेल के समय आँख से संपर्क का अभ्यास (10 मिनट)',
      category: 'श्रेणी',
      selectCategory: 'श्रेणी चुनें',
      instructions: 'निर्देश',
      instructionsHint: 'अभिभावक के लिए वैकल्पिक मार्गदर्शन',
      submit: 'कार्य असाइन करें',
    },
  },
  login: {
    welcome: 'वापसी पर स्वागत है',
    subtitle: 'अपने पोर्टल में साइन इन करें',
    selectRole: 'अपनी भूमिका चुनें',
    email: 'ईमेल पता',
    emailPlaceholder: 'अपना ईमेल दर्ज करें',
    password: 'पासवर्ड',
    passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
    forgotPassword: 'पासवर्ड भूल गए?',
    roles: {
      PARENT: { title: 'अभिभावक पोर्टल', blurb: 'अपने बच्चे की प्रगति देखें' },
    },
  },
  forgot: {
    title: 'अपना पासवर्ड रीसेट करें',
    subtitle: 'अपना ईमेल दर्ज करें, हम आपको एक रीसेट लिंक भेजेंगे।',
    submit: 'रीसेट लिंक भेजें',
    sentTitle: 'अपना ईमेल देखें',
    sentBody:
      'यदि उस ईमेल के लिए कोई खाता मौजूद है, तो हमने पासवर्ड रीसेट करने का लिंक भेज दिया है। लिंक 1 घंटे में समाप्त हो जाएगा।',
    backToLogin: 'लॉगिन पर वापस जाएँ',
  },
  pagination: {
    prev: 'पिछला',
    next: 'अगला',
    pageOf: 'पृष्ठ {{page}} / {{total}}',
  },
  account: {
    changePassword: {
      title: 'पासवर्ड बदलें',
      subtitle: 'अपने खाते का पासवर्ड अपडेट करें।',
      current: 'वर्तमान पासवर्ड',
      new: 'नया पासवर्ड',
      submit: 'पासवर्ड अपडेट करें',
      success: 'आपका पासवर्ड अपडेट कर दिया गया है।',
      error: 'पासवर्ड नहीं बदला जा सका। अपना वर्तमान पासवर्ड जाँचें और पुनः प्रयास करें।',
    },
  },
  setPassword: {
    title: 'अपना पासवर्ड सेट करें',
    subtitle: 'अपना खाता सक्रिय करने के लिए एक पासवर्ड चुनें।',
    newPassword: 'नया पासवर्ड',
    confirmPassword: 'पासवर्ड की पुष्टि करें',
    submit: 'पासवर्ड सेट करें',
    missingToken: 'यह लिंक अमान्य है या इसमें टोकन नहीं है।',
    error: 'यह लिंक अमान्य है या समाप्त हो गया है। क्लिनिक से फिर से भेजने को कहें।',
    doneTitle: 'पासवर्ड सेट हो गया',
    doneBody: 'आपका खाता सक्रिय है। अब आप साइन इन कर सकते हैं।',
    goToLogin: 'लॉगिन पर जाएं',
  },
  nav: {
    dashboard: 'डैशबोर्ड',
    inbox: 'इनबॉक्स',
    progress: 'प्रगति',
    sessions: 'सत्र',
    availability: 'उपलब्धता',
    homeTasks: HOME_TASKS,
    reports: 'रिपोर्ट',
    billing: 'बिलिंग',
    catalogue: 'सूची',
    messages: 'संदेश',
    staffUsers: 'स्टाफ उपयोगकर्ता',
    users: 'उपयोगकर्ता',
  },
  inbox: {
    title: 'इनबॉक्स',
    subtitle: 'आपकी सूचनाएँ और अलर्ट',
    all: 'सभी',
    unread: 'अपठित',
    markAllRead: 'सभी को पढ़ा हुआ चिह्नित करें',
    empty: 'कोई नई सूचना नहीं',
    viewAll: 'सभी सूचनाएँ देखें',
  },
  finance: {
    dashboard: {
      invoiced: 'कुल इनवॉइस',
      collected: 'वसूल किया गया',
      outstanding: 'बकाया',
      invoices: 'इनवॉइस',
      revenueTrend: 'राजस्व',
      revenueTrendSub: 'वसूल किया गया, पिछले 6 महीने',
    },
    invoices: {
      title: 'इनवॉइस',
      count: '{{count}} इनवॉइस',
      add: 'नया इनवॉइस',
      addSubtitle: 'क्लाइंट के लिए नया इनवॉइस बनाएँ',
      empty: 'अभी तक कोई इनवॉइस नहीं।',
      searchPlaceholder: 'इनवॉइस नंबर या नोट्स से खोजें…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      recordPayment: 'भुगतान दर्ज करें',
      recordPaymentSubtitle: 'इस इनवॉइस के विरुद्ध भुगतान दर्ज करें',
      col: {
        number: 'इनवॉइस',
        client: 'क्लाइंट',
        amount: 'राशि',
        balance: 'शेष',
        due: 'देय तिथि',
        status: 'स्थिति',
      },
    },
    reports: {
      title: 'रिपोर्ट',
      count: '{{count}} रिपोर्ट',
    },
    catalogue: {
      title: 'सेवा सूची',
      count: '{{count}} सेवाएँ',
      add: 'सेवा जोड़ें',
      addTitle: 'सेवा जोड़ें',
      editTitle: 'सेवा संपादित करें',
      subtitle: 'डिफ़ॉल्ट मूल्य वाली एक सेवा; इनवॉइस इसी से मूल्य लेते हैं।',
      name: 'सेवा का नाम',
      price: 'डिफ़ॉल्ट मूल्य (₹)',
      priceHint: 'जैसे 2500',
      description: 'विवरण (वैकल्पिक)',
      save: 'सेवा सहेजें',
      empty: 'अभी कोई सेवा नहीं — पहला शुल्क-सूची आइटम जोड़ें।',
      edit: 'सेवा संपादित करें',
      delete: 'सेवा हटाएँ',
      inactive: 'निष्क्रिय',
    },
  },
  reports: {
    recent: 'हाल की रिपोर्ट',
    generate: 'रिपोर्ट बनाएं',
    generateError: 'वह रिपोर्ट शुरू नहीं हो सकी। कृपया पुनः प्रयास करें।',
    loading: 'रिपोर्ट लोड हो रही हैं…',
    error: 'रिपोर्ट लोड नहीं हो सकीं।',
    empty: 'अभी कोई रिपोर्ट नहीं — ऊपर एक बनाएं।',
    download: 'डाउनलोड',
    field: {
      type: 'रिपोर्ट प्रकार',
      client: 'क्लाइंट',
      selectClient: 'एक क्लाइंट चुनें',
      from: 'से',
      to: 'तक',
    },
    type: {
      client_progress: 'क्लाइंट प्रगति',
      attendance: 'उपस्थिति',
      revenue: 'राजस्व',
    },
    status: {
      queued: 'कतार में',
      running: 'बन रही है…',
      succeeded: 'तैयार',
      failed: 'विफल',
    },
    col: {
      report: 'रिपोर्ट',
      status: 'स्थिति',
      created: 'बनाया गया',
      action: '',
    },
  },
  invoiceStatus: {
    draft: 'ड्राफ्ट',
    sent: 'भेजा गया',
    partial: 'आंशिक',
    paid: 'भुगतान किया',
    overdue: 'अतिदेय',
    void: 'रद्द',
  },
  payroll: {
    title: 'पेरोल',
    count: '{{count}} रन',
    add: 'नया पेरोल रन',
    addSubtitle: 'एक वेतन अवधि के लिए पेरोल रन बनाएँ',
    empty: 'अभी तक कोई पेरोल रन नहीं।',
    colPeriod: 'अवधि',
    colTotal: 'कुल',
    colStatus: 'स्थिति',
    form: {
      period: 'वेतन अवधि',
      periodHint: 'जैसे जून 2026',
      start: 'अवधि प्रारंभ',
      end: 'अवधि समाप्त',
      notes: 'टिप्पणियाँ',
      submit: 'रन बनाएँ',
      therapist: 'चिकित्सक',
      selectTherapist: 'चिकित्सक चुनें',
      gross: 'सकल वेतन (₹)',
      sessions: 'सत्र',
      addSubmit: 'पेस्लिप जोड़ें',
    },
    detail: {
      title: 'पेरोल रन',
      total: 'कुल: {{amount}}',
      addPayslip: 'पेस्लिप जोड़ें',
      addSubtitle: 'इस रन में चिकित्सक पेस्लिप जोड़ें',
      process: 'प्रोसेस करें',
      empty: 'अभी तक कोई पेस्लिप नहीं।',
      colTherapist: 'चिकित्सक',
      colSessions: 'सत्र',
      colGross: 'सकल',
    },
  },
  payrollStatus: {
    draft: 'ड्राफ्ट',
    processed: 'प्रोसेस किया गया',
    paid: 'भुगतान किया',
  },
  paymentMethod: {
    cash: 'नकद',
    card: 'कार्ड',
    upi: 'UPI',
    bank_transfer: 'बैंक हस्तांतरण',
    razorpay: 'Razorpay',
  },
  home: {
    assign: 'कार्य असाइन करें',
    assignSubtitle: 'इस क्लाइंट के लिए घर-कार्यक्रम कार्य जोड़ें',
  },
  analytics: {
    title: 'विश्लेषण',
    subtitle: 'एक नज़र में क्लिनिक प्रदर्शन',
    financeSubtitle: 'एक नज़र में राजस्व और वसूली',
    totalClients: 'कुल क्लाइंट',
    completedSessions: 'पूर्ण सत्र',
    sessionsByStatus: 'स्थिति अनुसार सत्र',
    clientsByStatus: 'स्थिति अनुसार क्लाइंट',
    invoicesByStatus: 'स्थिति अनुसार इनवॉइस',
    collections: 'इनवॉइस बनाम वसूली (₹)',
  },
  settings: {
    title: 'सेटिंग्स',
    subtitle: 'अपनी क्लिनिक प्रोफ़ाइल प्रबंधित करें',
    name: 'क्लिनिक नाम',
    legalName: 'कानूनी नाम',
    email: 'संपर्क ईमेल',
    phone: 'संपर्क फ़ोन',
    timezone: 'समय क्षेत्र',
    gstin: 'GSTIN',
    address: 'पता',
    line1: 'पता पंक्ति 1',
    line2: 'पता पंक्ति 2',
    city: 'शहर',
    state: 'राज्य',
    postalCode: 'पिन कोड',
    country: 'देश (ISO)',
    save: 'परिवर्तन सहेजें',
    saved: 'सेटिंग्स सहेजी गईं।',
    tabs: { profile: 'प्रोफ़ाइल', notifications: 'सूचनाएँ', security: 'सुरक्षा' },
    notifications: {
      sessionReminders: 'सत्र अनुस्मारक',
      sessionRemindersDesc: 'आगामी सत्रों से पहले ईमेल अनुस्मारक।',
      paymentAlerts: 'भुगतान अलर्ट',
      paymentAlertsDesc: 'चालान भुगतान या देय होने पर सूचित करें।',
      progressReports: 'प्रगति रिपोर्ट',
      progressReportsDesc: 'प्रगति रिपोर्ट तैयार होने पर सूचित करें।',
      systemUpdates: 'सिस्टम अपडेट',
      systemUpdatesDesc: 'प्लेटफ़ॉर्म और रखरखाव घोषणाएँ।',
    },
    security: {
      subtitle: 'अपने खाते का पासवर्ड बदलें। आप इस डिवाइस पर साइन इन रहेंगे।',
    },
  },
  messages: {
    title: 'संदेश',
    new: 'नया संदेश',
    newSubtitle: 'किसी संपर्क के साथ बातचीत शुरू करें',
    empty: 'अभी तक कोई संदेश नहीं।',
    conversation: 'बातचीत',
    composer: 'संदेश लिखें…',
    noContacts: 'संदेश भेजने के लिए कोई संपर्क उपलब्ध नहीं।',
    selectContact: 'संपर्क चुनें',
    startChat: 'चैट शुरू करें',
    pickConversation: 'चैट शुरू करने के लिए बातचीत चुनें',
  },
  homeCategory: {
    social: 'सामाजिक',
    sensory: 'संवेदी',
    cognitive: 'संज्ञानात्मक',
    communication: 'संचार',
    motor: 'मोटर',
    other: 'अन्य',
  },
  sa: {
    dashboard: {
      title: 'प्लेटफ़ॉर्म अवलोकन',
      subtitle: 'TherapyOS पर सभी क्लिनिक एक नज़र में',
      empty: 'अभी तक कोई डेटा नहीं।',
      totalClinics: 'कुल क्लिनिक',
      activeClinics: 'सक्रिय क्लिनिक',
      mrr: 'मासिक आवर्ती राजस्व (MRR)',
      health: 'स्वास्थ्य (G / A / R)',
      healthHint: 'हरा / पीला / लाल',
      topBySessions: 'सत्रों के अनुसार शीर्ष क्लिनिक',
      topByRevenue: 'राजस्व के अनुसार शीर्ष क्लिनिक (₹)',
    },
    clinics: {
      title: 'क्लिनिक',
      count: '{{count}} क्लिनिक',
      add: 'नया क्लिनिक',
      empty: 'अभी तक कोई क्लिनिक नहीं।',
      searchPlaceholder: 'नाम, योजना या स्थिति से खोजें…',
      colName: CLIENT,
      colStatus: STATUS,
      colPlan: PLAN,
      colMrr: MRR,
      colTherapists: THERAPISTS,
      colActiveClients: ACTIVE_CLIENTS,
    },
    subscriptions: {
      title: 'सदस्यताएं',
      count: '{{count}} क्लिनिक',
      empty: 'अभी कोई सदस्यता नहीं।',
      col: {
        clinic: 'क्लिनिक',
        plan: PLAN,
        status: 'स्थिति',
        mrr: MRR,
        renews: 'नवीनीकरण / ट्रायल समाप्ति',
        actions: '',
      },
      markPaid: 'भुगतान चिह्नित करें',
      status: {
        active: 'सक्रिय',
        trialing: 'ट्रायल',
        past_due: 'बकाया',
        suspended: 'निलंबित',
        cancelled: 'रद्द',
        none: 'कोई प्लान नहीं',
      },
    },
    usage: {
      title: 'उपयोग और स्वास्थ्य',
      healthSummary: '{{green}} हरा · {{amber}} एम्बर · {{red}} लाल',
      empty: 'अभी कोई उपयोग डेटा नहीं।',
      col: {
        clinic: 'क्लिनिक',
        health: 'स्वास्थ्य',
        sessions: 'सत्र (30द)',
        whatsapp: 'व्हाट्सएप',
        sms: 'एसएमएस',
        pdfs: 'PDFs',
        storage: 'स्टोरेज',
        users: 'सक्रिय उपयोगकर्ता',
      },
      health: {
        green: 'स्वस्थ',
        amber: 'जोखिम',
        red: 'गंभीर',
        unknown: 'अज्ञात',
      },
    },
    staff: {
      title: 'स्टाफ उपयोगकर्ता',
      count: '{{count}} स्टाफ खाते',
      add: 'स्टाफ जोड़ें',
      empty: 'अभी कोई स्टाफ खाता नहीं।',
      active: 'सक्रिय',
      inactive: 'निष्क्रिय',
      enable: 'सक्षम करें',
      disable: 'अक्षम करें',
      col: {
        member: 'सदस्य',
        role: 'भूमिका',
        status: 'स्थिति',
        lastLogin: 'अंतिम लॉगिन',
        actions: '',
      },
      role: {
        PLATFORM_OWNER: 'प्लेटफॉर्म स्वामी',
        CUSTOMER_SUCCESS: 'ग्राहक सफलता',
        SUPPORT: 'सहायता',
        READONLY_AUDITOR: 'केवल-पढ़ने वाला ऑडिटर',
      },
      form: {
        fullName: 'पूरा नाम',
        email: 'ईमेल',
        role: 'भूमिका',
        submit: 'खाता बनाएं',
        error: 'खाता नहीं बना सका (ईमेल पहले से उपयोग में हो सकता है)।',
      },
      created: {
        title: 'खाता बनाया गया',
        intro: '{{email}} के साथ ये क्रेडेंशियल सुरक्षित रूप से साझा करें।',
        copy: 'कॉपी',
        warning: 'एक बार दिखाया गया — पहले लॉगिन पर पासवर्ड बदलें।',
        done: 'हो गया',
      },
    },
    clinicStatus: {
      active: 'सक्रिय',
      trial: TRIAL,
      trialing: TRIAL,
      suspended: 'निलंबित',
      cancelled: 'रद्द',
      past_due: 'बकाया',
    },
    detail: {
      back: 'क्लिनिक पर वापस जाएँ',
      notFound: 'क्लिनिक नहीं मिला।',
      overview: 'अवलोकन',
      legalName: LEGAL_NAME,
      contactEmail: CONTACT_EMAIL,
      contactPhone: CONTACT_PHONE,
      timezone: 'समय क्षेत्र',
      createdAt: 'जोड़ा गया',
      subscription: SUBSCRIPTION,
      noSubscription: 'कोई सक्रिय सदस्यता नहीं।',
      plan: PLAN,
      subStatus: STATUS,
      mrr: MRR,
      periodEnd: 'नवीनीकरण',
      therapists: THERAPISTS,
      activeClients: ACTIVE_CLIENTS,
      sessions: 'सत्र',
      invoiced: 'चालान',
      recentAudit: 'हाल की गतिविधि',
      noAudit: 'कोई हाल की गतिविधि नहीं।',
    },
    audit: {
      title: 'ऑडिट लॉग',
      subtitle: 'सभी क्लिनिक में गतिविधि',
      empty: 'इन फ़िल्टर के लिए कोई प्रविष्टि नहीं।',
      allClinics: 'सभी क्लिनिक',
      filterClinic: 'क्लिनिक से फ़िल्टर करें',
      filterAction: 'क्रिया से फ़िल्टर करें…',
      filterFrom: 'से दिनांक',
      filterTo: 'तक दिनांक',
      colTime: 'समय',
      colClinic: CLIENT,
      colActor: 'कर्ता',
      colAction: ACTION,
      colEntity: 'इकाई',
    },
    settings: {
      title: 'प्लेटफ़ॉर्म सेटिंग्स',
      subtitle: 'फ़ीचर फ़्लैग और प्लेटफ़ॉर्म कॉन्फ़िगरेशन',
      flags: 'फ़ीचर फ़्लैग',
      empty: 'अभी कोई फ़ीचर फ़्लैग नहीं। नीचे एक जोड़ें।',
      enabled: 'सक्षम',
      disabled: 'अक्षम',
      enable: 'सक्षम करें',
      disable: 'अक्षम करें',
      addKey: 'एक फ़ीचर फ़्लैग जोड़ें',
      addPlaceholder: 'जैसे parent_app_v2',
      add: 'जोड़ें',
      col: {
        key: 'फ़्लैग',
        state: 'स्थिति',
        updated: 'अपडेट किया गया',
      },
    },
    wizard: {
      title: 'क्लिनिक जोड़ें',
      subtitle: 'क्लिनिक, उसका पहला व्यवस्थापक और सदस्यता बनाएँ',
      stepOf: 'चरण {{step}} / {{total}}',
      clinicName: 'क्लिनिक का नाम',
      legalName: LEGAL_NAME,
      contactEmail: CONTACT_EMAIL,
      contactPhone: CONTACT_PHONE,
      line1: 'पता पंक्ति 1',
      city: 'शहर',
      state: 'राज्य',
      postalCode: 'पिन कोड',
      country: 'देश (ISO 3166-1 alpha-2)',
      adminName: 'पहले व्यवस्थापक का नाम',
      adminEmail: 'पहले व्यवस्थापक का ईमेल',
      planSlug: PLAN,
      trialDays: 'परीक्षण दिवस (वैकल्पिक)',
      back: 'पीछे',
      next: 'अगला',
      submit: 'क्लिनिक जोड़ें',
    },
  },
} as const;
